/**
 * What a channel's `external_user_id` actually IS — single source of truth.
 *
 * Isomorphic (type-only import) so the reply pipeline, CRM sync and client UI
 * share one rule instead of each testing `channelType === 'whatsapp'`.
 *
 * Total `Record<ChannelType, ...>` like `CHANNEL_TEXT_LIMITS`: a new ChannelType
 * fails typecheck here, forcing a deliberate call rather than a silent "opaque".
 */

import type { ChannelType } from '../types'

export type ExternalUserIdKind = 'phone' | 'opaque'

/**
 * `phone` — the id is the customer's real number (WhatsApp wa_id: E.164 sans '+'),
 * so it is known from the first inbound and is contactable.
 * `opaque` — PSID / IGSID / Telegram chat id / widget session / TikTok
 * conversation id: platform-scoped handles that identify a thread, not a person
 * we can dial.
 */
export const CHANNEL_USER_ID_KIND: Record<ChannelType, ExternalUserIdKind> = {
  instagram: 'opaque',
  facebook: 'opaque',
  telegram: 'opaque',
  whatsapp: 'phone',
  tiktok: 'opaque',
  web_widget: 'opaque',
  web_demo: 'opaque',
}

/**
 * Channels whose `external_user_id` IS the customer's phone number. Exported so
 * every search that matches a number against it reads one list — the operator
 * inbox and the unified inbox disagreeing about which channels those are would
 * make the same query find different people.
 */
export const PHONE_ID_CHANNELS = Object.entries(CHANNEL_USER_ID_KIND)
  .filter(([, kind]) => kind === 'phone')
  .map(([channel]) => channel as ChannelType)

/** Customer phone in E.164, or null when the channel's id is not a number. */
export function customerPhoneFromExternalUserId(
  channelType: ChannelType,
  externalUserId: string | null | undefined
): string | null {
  if (CHANNEL_USER_ID_KIND[channelType] !== 'phone' || !externalUserId) return null
  const digits = externalUserId.replace(/\D/g, '')
  return digits ? `+${digits}` : null
}
